/*
 * Campos obligatorios que un registro tiene vacíos, con el mismo criterio que la
 * validación del back (hooks/default/hook_validate.js): el `required` de la definición
 * más las reglas visuales «Hacer obligatorio» (p. ej. Logistic team si el Type es
 * Client). Los campos ocultos (`hide`) no cuentan.
 *
 * El guardado se puede forzar con «Guardar de todos modos», así que quedan registros
 * incompletos: el listado y la ficha los marcan para que alguien los complete (#473).
 */

function isEmptyValue(value) {
  if (value === null || value === undefined || value === "") return true;
  if (Array.isArray(value))
    return value.filter((v) => v !== null && v !== "").length === 0;
  return false;
}

/** Listas y referencias se comparan por id, vengan pobladas o no. */
function idOf(value) {
  return value &&
    typeof value === "object" &&
    !Array.isArray(value) &&
    value._id != null
    ? String(value._id)
    : value;
}

function ruleConditionHolds(rule, rawValue) {
  const value = idOf(rawValue);
  const empty = isEmptyValue(value);
  const expected = rule.value == null ? "" : String(rule.value);
  switch (rule.condition) {
    case "equals":
      return !empty && String(value) === expected;
    case "notEquals":
      return empty || String(value) !== expected;
    case "contains":
      return !empty && String(value).includes(expected);
    case "notContains":
      return empty || !String(value).includes(expected);
    case "isEmpty":
      return empty;
    case "isNotEmpty":
      return !empty;
    default:
      return false;
  }
}

/** Reglas «Hacer obligatorio» sobre campos de primer nivel (las de subcampos no se evalúan). */
function requireRules(definition) {
  const keys = new Set(
    ((definition && definition.fields) || []).map((f) => f && f.key)
  );
  return ((definition && definition.visualRules) || []).filter(
    (r) => r && r.action === "require" && keys.has(r.targetField)
  );
}

/**
 * Claves que el listado tiene que pedir (`fields=`) para poder evaluar el aviso aunque
 * esas columnas no estén a la vista.
 */
export function requiredCheckFieldKeys(definition) {
  const keys = new Set();
  for (const f of (definition && definition.fields) || []) {
    if (f && f.required && !f.hide) keys.add(f.key);
  }
  for (const rule of requireRules(definition)) {
    keys.add(rule.targetField);
    (rule.fieldsToToggle || []).forEach((k) => keys.add(k));
  }
  return [...keys];
}

/**
 * Definiciones de los campos obligatorios vacíos del registro, en el orden de la definición.
 * @param {{ fields?: object[], visualRules?: object[] }} definition
 * @param {object} item
 * @returns {object[]}
 */
export function missingRequiredFields(definition, item) {
  if (!definition || !item) return [];
  const byRule = new Set();
  for (const rule of requireRules(definition)) {
    if (ruleConditionHolds(rule, item[rule.targetField])) {
      (rule.fieldsToToggle || []).forEach((k) => byRule.add(k));
    }
  }
  return (definition.fields || []).filter(
    (f) =>
      f &&
      !f.hide &&
      (f.required || byRule.has(f.key)) &&
      isEmptyValue(item[f.key])
  );
}
